import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/lib/api";
import type {
  ApproveIntakeRequestBody,
  ApproveProjectRequestBody,
  IntakeListResponse,
  OscarIntake,
  Phase0UploadResponse,
  RejectIntakeInput,
} from "@/types/intake";

/**
 * Intake domain hooks — Oscar-sourced pending_approval projects.
 *
 *   - GET  /api/projects?status=pending_approval&source=oscar → IntakeListResponse
 *   - GET  /api/projects/{id}                                 → OscarIntake (detail fallback)
 *   - POST /api/projects/{id}/approve                         → approve + provision (existing)
 *   - POST /api/projects/{id}/reject                          → reject with reason (existing)
 *   - POST /api/projects/{id}/phase0/upload                   → presigned S3 PUT URL (existing)
 *
 * Approve/reject invalidate both the intakes list and the main projects list so the approved deal
 * disappears from Intakes and appears in Projects without a manual refresh.
 */

const INTAKES_KEY = ["intakes"] as const;

/** Fetch all Oscar intakes (pending_approval, source=oscar). Sorted newest-first by the caller. */
export function useIntakes() {
  const client = useApiClient();

  return useQuery<IntakeListResponse>({
    queryKey: INTAKES_KEY,
    queryFn: async () => {
      const params = new URLSearchParams({
        status: "pending_approval",
        source: "oscar",
      });
      const response = await client.get<IntakeListResponse>(
        `/api/projects?${params.toString()}`,
      );
      return response.data;
    },
  });
}

/**
 * Fetch a single intake by Jira key for the full-page wizard.
 *
 * Cache-first: seeds instantly from the already-loaded `['intakes']` list (which carries the deal
 * intelligence), then fetches GET /api/projects/{jiraKey} for the authoritative record and merges
 * it over the cached values. This keeps the wizard usable on a hard refresh / deep link while
 * still showing Oscar-only fields that only the list currently provides.
 */
export function useIntake(jiraKey: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useQuery<OscarIntake>({
    queryKey: ["intake", jiraKey],
    enabled: !!jiraKey,
    queryFn: async () => {
      const cachedList =
        queryClient.getQueryData<IntakeListResponse>(INTAKES_KEY);
      const cached = cachedList?.projects.find((p) => p.jira_key === jiraKey);

      const response = await client.get<OscarIntake>(
        `/api/projects/${jiraKey}`,
        {
          _suppressErrorToast: true,
        },
      );
      // Detail endpoint is authoritative for base fields; the cached list still holds Oscar-only
      // deal-intelligence fields the detail endpoint does not (yet) return — keep both.
      return { ...cached, ...response.data } as OscarIntake;
    },
    // If the detail fetch fails but we have the list entry, fall back to it.
    placeholderData: () => {
      const cachedList =
        queryClient.getQueryData<IntakeListResponse>(INTAKES_KEY);
      return cachedList?.projects.find((p) => p.jira_key === jiraKey);
    },
  });
}

/**
 * POST /api/projects/{jiraKey}/approve — approve an Oscar intake with the wizard-collected project
 * setup + team (IntakeWizard), or a pending project without a team array (ApprovalModal). Fires
 * provisioning (GitHub, Slack, Jira, Clockify) + CASDM template seeding server-side. Invalidates the
 * intakes + projects lists on success.
 *
 * V057: sow_hours / planned_kickoff_date / expected_completion_date are required by both body
 * types; the backend 400s with `details.missing` when one has no effective value.
 */
export function useApproveIntake(jiraKey: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      body: ApproveIntakeRequestBody | ApproveProjectRequestBody,
    ): Promise<OscarIntake> => {
      const response = await client.post<
        { project: OscarIntake } | OscarIntake
      >(`/api/projects/${jiraKey}/approve`, body);
      // Approve handler returns { project }; be tolerant of a bare project too.
      const data = response.data as { project?: OscarIntake };
      return (data.project ?? (response.data as OscarIntake)) as OscarIntake;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INTAKES_KEY });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/** POST /api/projects/{jiraKey}/reject — reject an Oscar intake with a reason. */
export function useRejectIntake(jiraKey: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RejectIntakeInput) => {
      const response = await client.post(
        `/api/projects/${jiraKey}/reject`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INTAKES_KEY });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/**
 * Upload an additional Phase 0 document for an intake.
 *
 * Two-step: (1) POST /api/projects/{jiraKey}/phase0/upload to mint a presigned S3 PUT URL, then
 * (2) PUT the file straight to S3 with a bare fetch (the presigned URL must NOT carry our
 * Authorization header, so we deliberately bypass the axios client for the PUT). Resolves with the
 * uploaded file name + s3_key for display.
 *
 * `size_bytes` and the returned `content_type` are both required by CR §6.1 SEC-4: the server signs
 * `content-length` and `content-type` into the URL, so the PUT must match both exactly or S3 returns
 * `SignatureDoesNotMatch`. `Content-Length` is a forbidden header for `fetch`, but the browser sets it
 * from the `File` body — which is the same number we declared.
 */
export function useUploadPhase0Doc(jiraKey: string) {
  const client = useApiClient();

  return useMutation({
    mutationFn: async (
      file: File,
    ): Promise<{ name: string; s3_key: string }> => {
      const presign = await client.post<Phase0UploadResponse>(
        `/api/projects/${jiraKey}/phase0/upload`,
        {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        },
      );
      const { upload_url, s3_key, content_type } = presign.data;

      const put = await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": content_type },
      });
      if (!put.ok) {
        throw new Error(`Upload failed (${put.status})`);
      }
      return { name: file.name, s3_key };
    },
  });
}
